/* =============================================================================
 * Copyright (c) 2026 SKYXOPS Corp. All rights reserved.
 *
 * This file is proprietary and confidential. Unauthorized copying,
 * distribution, modification, or use of this file, via any medium,
 * is strictly prohibited without the express written permission of
 * SKYXOPS Corp.
 * ============================================================================= */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle, AppShellV2, Skeleton, TopBar } from '@skyxops/ui';
import { ArrowLeft, Bell, Clock, Monitor, Palette, ShieldCheck, User } from 'lucide-react';
import {
  ApiError, Branding, getProfile, getTiles, setToken, type Profile, type TilesResponse,
} from '../api';
import { useTopBarSlots } from '../components/TopBarContents';
import { IdentitySection } from '../components/profile/IdentitySection';
import { SecuritySection } from '../components/profile/SecuritySection';
import { ThemeSection } from '../components/profile/ThemeSection';

type Section = 'profile' | 'security' | 'appearance';

interface Props {
  branding: Branding | null;
  onLogout: () => void;
}

export function ProfilePage({ branding, onLogout }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [principal, setPrincipal] = useState<TilesResponse['principal'] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [section, setSection] = useState<Section>(location.hash === '#security' ? 'security' : 'profile');

  useEffect(() => {
    getProfile()
      .then(setProfile)
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
    getTiles().then((t) => setPrincipal(t.principal)).catch(() => { /* topbar name optional */ });
  }, []);

  function logout() { setToken(null); onLogout(); }
  const { left, right, banner, headerStyle } = useTopBarSlots({ principal, branding, onLogout: logout });

  const NAV: { id: Section; label: string; Icon: typeof User }[] = [
    { id: 'profile', label: 'Profile', Icon: User },
    { id: 'security', label: 'Security', Icon: ShieldCheck },
    { id: 'appearance', label: 'Appearance', Icon: Palette },
  ];
  const SOON: { label: string; Icon: typeof User }[] = [
    { label: 'Active sessions', Icon: Monitor }, { label: 'Notifications', Icon: Bell }, { label: 'Activity log', Icon: Clock },
  ];

  return (
    <AppShellV2 topBar={<TopBar left={left} right={right} banner={banner} style={headerStyle} />}>
      <button onClick={() => navigate('/')}
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Back to apps
      </button>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Account settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Manage your profile, security, and preferences for your SKYXOPS account.</p>
      </div>

      {error && <Alert variant="destructive" className="mb-6"><AlertTitle>Couldn’t load your account</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>}

      <div className="grid grid-cols-12 gap-6">
        <aside className="col-span-12 md:col-span-3">
          <nav className="sticky top-4 rounded-xl border border-border bg-card p-1.5 text-sm">
            {NAV.map(({ id, label, Icon }) => (
              <button key={id} type="button" onClick={() => setSection(id)}
                className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left ${
                  section === id ? 'bg-accent font-semibold text-foreground' : 'text-muted-foreground hover:bg-accent'
                }`}>
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
            <div className="my-1.5 border-t border-border" />
            <div className="px-3 pb-1 pt-1 text-[0.65rem] font-semibold uppercase tracking-wider text-muted-foreground">Coming soon</div>
            {SOON.map(({ label, Icon }) => (
              <div key={label} className="flex w-full cursor-default items-center gap-2.5 rounded-lg px-3 py-2 text-muted-foreground opacity-50">
                <Icon className="h-4 w-4" /> {label}
                <span className="ml-auto rounded bg-accent px-1.5 py-0.5 text-[0.6rem]">Soon</span>
              </div>
            ))}
          </nav>
        </aside>

        <div className="col-span-12 space-y-6 md:col-span-9">
          {!profile ? (
            <Skeleton className="h-64 w-full rounded-xl" />
          ) : section === 'profile' ? (
            <IdentitySection profile={profile} onSaved={setProfile} />
          ) : section === 'security' ? (
            <SecuritySection profile={profile} onProfileChange={setProfile} />
          ) : (
            <ThemeSection profile={profile} />
          )}
        </div>
      </div>
    </AppShellV2>
  );
}
